import { ExternalLink, ImageIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { WhatsAppPreviewButton } from "./whatsappMessagePreview";

/**
 * The WhatsApp message mock-up, shared by the broadcast composer and the
 * template builder.
 *
 * Both screens used to draw this markup themselves, which is how the composer
 * ended up able to show buttons only after a server round trip while the
 * template builder showed them live. One component, one look, and a live
 * preview is now just a matter of passing freshly rendered parts.
 */

export type WhatsAppMessageMedia = {
  format: string;
  url?: string | null;
  label?: string | null;
};

function MediaPanel({ media }: { media: WhatsAppMessageMedia }) {
  const label = media.label || media.format;
  const url = media.url?.trim();

  if (url && media.format === "IMAGE") {
    return (
      <img
        alt={label}
        className="aspect-video w-full rounded-md border object-cover"
        src={url}
      />
    );
  }
  if (url && media.format === "VIDEO") {
    return (
      <video
        className="aspect-video w-full rounded-md border object-cover"
        controls
        src={url}
      />
    );
  }
  if (url && media.format === "DOCUMENT") {
    return (
      <a
        className="flex min-h-16 items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm"
        href={url}
        rel="noreferrer"
        target="_blank"
      >
        <span className="min-w-0 truncate">{label}</span>
        <ExternalLink className="size-4 shrink-0" />
      </a>
    );
  }
  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed bg-background text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <ImageIcon className="size-4" />
        <span>{label}</span>
      </div>
    </div>
  );
}

export function WhatsAppMessageBubble({
  media,
  header,
  body,
  footer,
  buttons = [],
  emptyBodyLabel,
  className,
  buttonAdornment,
}: {
  media?: WhatsAppMessageMedia | null;
  header?: string;
  body?: string;
  footer?: string;
  buttons?: WhatsAppPreviewButton[];
  /** Shown in place of the body while it is still empty. */
  emptyBodyLabel?: string;
  className?: string;
  /**
   * Optional badge rendered beside a button — the composer uses it to show
   * whether Felix will understand that button's label.
   */
  buttonAdornment?: (button: WhatsAppPreviewButton) => ReactNode;
}) {
  return (
    <div className={cn("rounded-md bg-muted/30 p-3", className)}>
      <div className="rounded-md bg-background p-3 shadow-sm">
        {media && <MediaPanel media={media} />}
        {header && <div className="mt-2 text-sm font-semibold">{header}</div>}
        <div
          className={cn(
            "mt-2 whitespace-pre-wrap text-sm leading-relaxed",
            !body && "text-muted-foreground",
          )}
        >
          {body || emptyBodyLabel}
        </div>
        {footer && (
          <div className="mt-2 text-xs text-muted-foreground">{footer}</div>
        )}
      </div>
      {buttons.length > 0 && (
        <div className="mt-2 grid gap-1">
          {buttons.map((button) => (
            <div
              className="flex min-h-9 items-center justify-center gap-2 rounded-md bg-background px-3 text-sm font-medium text-primary shadow-sm"
              key={`${button.type}-${button.text}-${button.url ?? ""}`}
            >
              {button.type === "URL" && <ExternalLink className="size-4" />}
              {button.text}
              {buttonAdornment?.(button)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
